import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import InfiniteScroll from 'react-infinite-scroll-component';
import CloseAlerts from 'Components/alerts/assets/svg/CloseAlerts';
import SkeletonCustom from 'Components/common/skeleton/SkeletonCustom';
import PeopleWidgetWithOutHolo from 'Components/common/components/PeopleWidgetWithOutHolo';
import {
  maxWidth, primaryHeadingColor, retina, secondaryFont, wMBold,
} from 'Utils/utilsStyle';
import {
  getLocalStorageValue,
  getTimezoneInGMT,
  MinWidthApp,
} from 'Utils/UtilityFunctions';
import { actions as personProfileSlice } from 'Slice/PersonProfileSlice';
import { actions as notesSlice } from 'Slice/NotesSlice';
import { actions as companyActions } from 'Slice/CompaniesSlice';
import { actions as flurryActions } from 'Slice/HelperSlice';
import { actions as uiSlice } from 'Slice/UISlice';
import {
  selectBuyerGroupNotes,
  selectNoteAdded,
  selectNoteDeleted,
  selectNoteEdited,
  selectPersonNotes,
} from 'Selector/NotesSelector';
import { selectOpenDrawer } from 'Selector/CertificationSelector';
import { selectToastNeedToOpen } from 'Selector/UISelector';
import { EntityNotes } from 'Types/NotesTypes';
import { Executive } from 'Types/PersonTypes';
import PeopleMoveReasonCard from './PeopleMoveReasonCard';

const Heading = styled.h3`
  font-family: ${secondaryFont};
  font-size: 46px;
  line-height: 44px;
  color: ${primaryHeadingColor};
  font-weight: ${wMBold};
  margin-bottom: 0px;
  padding: 45px 30px 48px 30px;
  border-bottom: 2px solid #EBEBEB;
  @media all and (max-width: ${maxWidth}) , ${retina} {
    padding: 34px 30px 34px 30px;
    font-size: 32px;
    line-height: 30px;
  }
  &.mb-head {
    margin-bottom: 30px;
  }
`;
const SkeletonWrapper = styled.div`
  padding: 15px 30px;
`;

type Props = {
  personId?: number,
  companyId?: number,
  isPeopleNotes?: boolean,
  isCompanyNotes?: boolean,
  isPersonCard?: boolean,
  showNotes?: boolean,
  inBuyerGroup?: boolean,
  isBuyerGroupNotes?: boolean,
  isGroupDetail?: boolean,
  isNotes?: boolean,
  groupIdParsed?: number,
  notesAvailable?: number,
  alertsNotificationId?: number,
  personYou?: Executive,
  onClose: () => void,
} & typeof defaultProps;

const defaultProps = {
  personId: 0,
  companyId: 0,
  isCompanyNotes: false,
  isPeopleNotes: false,
  showNotes: false,
  isPersonCard: false,
  inBuyerGroup: false,
  isBuyerGroupNotes: false,
  isGroupDetail: false,
  isNotes: false,
  groupIdParsed: 0,
  notesAvailable: 0,
  alertsNotificationId: 0,
  personYou: undefined as Executive | undefined,
};

const DrawerPeopleMoveReasonContent = function DrawerPeopleMoveReasonContent(props: Props) {
  const {
    personId,
    companyId,
    isPeopleNotes,
    isCompanyNotes,
    isPersonCard,
    showNotes,
    inBuyerGroup,
    isBuyerGroupNotes,
    isGroupDetail,
    isNotes,
    groupIdParsed,
    notesAvailable,
    alertsNotificationId,
    personYou,
    onClose,
  } = props;
  const dispatch = useDispatch();
  const [addNote, setAddNote] = useState(true);
  const [noteId, setNoteId] = useState(0);
  const [newNote, setNewNote] = useState<EntityNotes>();
  const [hasScrolled, setHasScrolled] = useState(false);

  const selectedNotesSelector = isBuyerGroupNotes ? selectBuyerGroupNotes : selectPersonNotes;
  const personNotes = useSelector(selectedNotesSelector);
  const {
    notes, notesLoading, notesEnded, notesPageNumber,
  } = personNotes;

  const noteDeleted = useSelector(selectNoteDeleted);
  const noteEdited = useSelector(selectNoteEdited);
  const noteAdded = useSelector(selectNoteAdded);
  const toastNeedToOpen = useSelector(selectToastNeedToOpen);
  const certificationDrawer = useSelector(selectOpenDrawer);

  const isMinWidth = MinWidthApp();

  const prevCertificationDrawer = useRef(certificationDrawer);
  useEffect(() => {
    if (prevCertificationDrawer.current === certificationDrawer) return;
    prevCertificationDrawer.current = certificationDrawer;
    if (certificationDrawer) onClose();
  }, [certificationDrawer]);

  useEffect(() => {
    if ((isPeopleNotes || isCompanyNotes || isNotes || showNotes)
      && !isGroupDetail && personYou) {
      dispatch(notesSlice.getPersonNotes({
        page: 1,
        ...(!isCompanyNotes && { personId }),
        ...(isCompanyNotes && { companyId }),
        type: 'notes',
        timezone: getTimezoneInGMT(),
      }));
    } else if (isGroupDetail && !isBuyerGroupNotes) {
      dispatch(notesSlice.getPersonNotes({
        groupId: groupIdParsed,
        personId,
        page: 1,
        type: 'reason',
        timezone: getTimezoneInGMT(),
      }));
    } else if (isGroupDetail && isBuyerGroupNotes && groupIdParsed) {
      dispatch(notesSlice.getBuyerGroupNotes({
        groupId: groupIdParsed,
        page: 1,
        timezone: getTimezoneInGMT(),
      }));
    }
  }, []);

  useEffect(() => {
    if (alertsNotificationId > 0 && !notesLoading && notes?.length > 0 && !hasScrolled) {
      const element = document.getElementById(`scroll-to-${alertsNotificationId}`);
      const scrollElement = document.querySelector('.data-append-wrapper') as HTMLElement;
      const isPresent = notes.filter(
        (note) => note.comment.noteId === alertsNotificationId,
      ).length > 0;
      if (element && isPresent) {
        setHasScrolled(true);
        setTimeout(() => {
          scrollElement.scrollTo({
            top: element.offsetTop - 170,
            behavior: 'smooth',
          });
          element.classList.add('highlight');
        }, 500);
        setTimeout(() => {
          element.classList.remove('highlight');
        }, 20000);
      } else if (!element && !isPresent) {
        if (toastNeedToOpen) {
          setHasScrolled(true);
          dispatch(uiSlice.isToastNeedToOpen(false));
          const notification = {
            message: 'Selected Note not found',
            proTip: false,
            rightPlacement: false,
            groupId: 0,
            classnameFlag: false,
          };
          dispatch(flurryActions.setNotification(notification));
        }
      }
    }
  }, [notesLoading]);

  useEffect(() => {
    if (noteAdded || noteDeleted || noteEdited) {
      dispatch(notesSlice.addPersonNoteFailed());
      dispatch(notesSlice.deletePersonNoteFailed());
      dispatch(notesSlice.editPersonNoteFailed());
    }
  }, [noteAdded, noteDeleted, noteEdited]);

  useEffect(() => {
    if (notesAvailable) {
      setAddNote(false);
    } else if (!isGroupDetail && !showNotes) {
      setAddNote(true);
    } else if (inBuyerGroup) {
      setAddNote(true);
    } else if (isBuyerGroupNotes) {
      setAddNote(false);
    } else {
      setAddNote(false);
    }
  }, [notesAvailable]);

  useEffect(() => {
    if (noteDeleted || noteAdded) {
      if (isCompanyNotes) {
        dispatch(companyActions.setCompanyNotesAvailable(notes.length ? 1 : 0));
      } else {
        dispatch(personProfileSlice.setPersonNotesAvailable(notes.length ? 1 : 0));
      }
    }
  }, [personNotes]);

  useEffect(() => {
    if (personYou) {
      setNewNote({
        comment: {
          person: personYou,
          text: '',
          type: '',
          dateTimeStamp: '',
          editable: false,
          noteId: 0,
          changed: false,
          entities: {},
        },
      });
    }
  }, [personYou]);

  const fetchMoreNotes = () => {
    if (notes?.length && !notesLoading && personYou) {
      if (isBuyerGroupNotes) {
        dispatch(notesSlice.getBuyerGroupNotes({
          groupId: groupIdParsed,
          page: notesPageNumber,
          timezone: getTimezoneInGMT(),
        }));
      } else {
        dispatch(notesSlice.getPersonNotes({
          ...(isGroupDetail && { groupId: groupIdParsed }),
          ...((isPeopleNotes || isPersonCard) && { personId }),
          ...(isCompanyNotes && { companyId }),
          page: notesPageNumber,
          type: isGroupDetail ? 'reason' : 'notes',
          timezone: getTimezoneInGMT(),
        }));
      }
    }
  };

  const getReasonSkeleton = () => (
    <>
      {[1, 2].map(() => (
        <SkeletonWrapper>
          <SkeletonCustom width="31px" height="31px" diameter="50%" marginSm="0px 9px 0px 0px" />
          <SkeletonCustom width="30%" height="20px" marginSm="0px 0px 5px 0px" />
          <br />
          <SkeletonCustom height="100px" margin="0px 0px 5px 0px" marginSm="0px 0px 5px 0px" />
        </SkeletonWrapper>
      ))}
    </>
  );

  const handleNewNote = () => {
    if (!addNote) setNoteId(0);
    setAddNote(!addNote);
  };

  const noteIdHandler = (id?: number) => {
    setNoteId(id || 0);
  };

  let headingText = 'Comments';
  if (isBuyerGroupNotes) {
    headingText = 'Notes/Reasons';
  } else if (!isGroupDetail) {
    headingText = 'Notes';
  }

  return (
    <>
      <span
        onClick={onClose}
        onKeyPress={onClose}
        tabIndex={0}
        role="button"
        aria-label="close"
        className="close-alerts reason-drawer-close"
      >
        <CloseAlerts />
      </span>
      <Heading>{headingText}</Heading>
      {!addNote && personYou && !isBuyerGroupNotes
        ? (
          <div
            className="add-note"
            onClick={handleNewNote}
            onKeyPress={handleNewNote}
            role="button"
            tabIndex={0}
          >
            <PeopleWidgetWithOutHolo
              diameter="31px"
              diameterSm="31px"
              diameterXsm="31px"
              logo={personYou?.personImage || ''}
              wrapperColor={getLocalStorageValue('personalitycolors') || ''}
              margin="0"
              ringPadding="2px"
              ringPaddingSm="2px"
            />
            <span
              className="add-note-input"
            >
              Add a note...
            </span>
          </div>
        ) : ''}
      {addNote && newNote && !isMinWidth && !isBuyerGroupNotes
        ? (
          <PeopleMoveReasonCard
            notes={newNote}
            isNewNoteOpen={addNote}
            newNote
            noteAbout={isCompanyNotes ? companyId : personId}
            isPeopleNotes={!isCompanyNotes}
            isCompanyNotes={isCompanyNotes}
            noEdit
            handleNewNoteCancel={handleNewNote}
            showReminderButton={!isGroupDetail}
          />
        ) : ''}
      <div className="h-100 unique-reason-class">
        <InfiniteScroll
          dataLength={notes.length}
          next={fetchMoreNotes}
          hasMore={!notesEnded}
          loader={false}
          className="data-append-wrapper"
          height="auto"
        >
          {notes?.length && personYou ? notes?.map((note: EntityNotes) => (
            <PeopleMoveReasonCard
              key={note.comment?.noteId}
              isPeopleNotes={!isCompanyNotes}
              isCompanyNotes={isCompanyNotes}
              isBuyerGroupNotes={isBuyerGroupNotes}
              noteAbout={isCompanyNotes ? companyId : personId}
              isNewNoteOpen={addNote}
              notes={note}
              handleNewNoteCancel={handleNewNote}
              getNoteId={noteIdHandler}
              editNoteId={noteId}
              showReminderButton={!isGroupDetail}
            />
          ))
            : null}
          {(notesLoading) && getReasonSkeleton()}
        </InfiniteScroll>
      </div>
    </>
  );
};

DrawerPeopleMoveReasonContent.defaultProps = defaultProps;
export default DrawerPeopleMoveReasonContent;
